import { Injectable } from '@angular/core';
import { Base64Service } from '../base64.service';
import { LoggerService } from '../logger.service';
import { BrowserSupportService } from '../browser-support.service';
import { IStorageMethod, IStorageOpts, ITransferItemDownload, IRenderData } from 'src/sync';

@Injectable({
  providedIn: 'root',
})
export class StorageMemoryService implements IStorageMethod {
  public maxsize = 1024 * 1024 * 500;

  private blobtype: number;
  private CONTAINER_BLOB = 1;
  private CONTAINER_ARRAY = 2;

  private options: { [key: string]: IStorageOpts } = {};
  private blobObj: { [key: string]: Blob[] } = {};
  private dataObj: { [key: string]: Array<number> } = {};

  constructor(
    private base64: Base64Service,
    private browser: BrowserSupportService,
    private log: LoggerService,
  ) {}

  public init(initOpts: IStorageOpts): Promise<number> {
    const start = Date.now();

    const key = this.bufferKey(initOpts.blobtype, initOpts.cachekey, initOpts.uniqid);
    this.options[key] = initOpts;
    if (this.browser.testBlob()) {
      this.blobObj[key] = [];
    } else {
      this.dataObj[key] = [];
    }

    if (this.options[key].filesize > this.maxsize) {
      return Promise.reject({ errcode: 3020 });
    } else {
      this.log.d('  - ' + (Date.now() - start) + ' ms to initialize');
      return Promise.resolve(1);
    }
  }

  public async writeChunk(
    tItem: ITransferItemDownload,
    bytearray: number[] | Uint8Array[],
    offset: number,
  ): Promise<number> {
    const key = this.bufferKey(tItem.blobtype, tItem.cachekey, tItem.uniqid);
    const decStart = Date.now();

    if (this.browser.testBlob()) {
      const curBlob = this.getBlobObj(key);
      let blob: Blob;
      if (typeof bytearray === 'object' && bytearray instanceof Uint8Array) {
        blob = new Blob([bytearray], { type: 'application/octet-binary' });
      } else {
        blob = new Blob(<any>bytearray, { type: 'application/octet-binary' });
      }
      curBlob.push(blob);
    } else {
      const curData = this.getDataObj(key);
      curData.push(...(bytearray as number[]));
    }
    this.log.d('  - ' + (Date.now() - decStart) + ' ms to write data');
    return Promise.resolve(1);
  }

  public async getRenderData(tItem: ITransferItemDownload): Promise<IRenderData> {
    const data: IRenderData = {
      type: '',
      url: '',
      blob: null,
      b64data: '',
    };
    const decStart = Date.now();

    const key = this.bufferKey(tItem.blobtype, tItem.cachekey, tItem.uniqid),
      opt = this.options[key];
    if (this.browser.testBlob()) {
      const cur = this.getBlobObj(key),
        blob = new Blob(cur, { type: this.options[key].mimetype });
      data.type = 'blob';
      data.url = URL.createObjectURL(blob);
      data.blob = blob;
      if (blob.size != opt.filesize && tItem.blobtype == 'btFILE') {
        this.log.warn(`Decrypted blobsize ${blob.size} is not equal to ${opt.filesize}`);
      }
    } else {
      const cur = this.getDataObj(key),
        b64 = this.base64.encodeBits(cur);
      data.type = 'b64';
      data.url = ['data:', opt.mimetype, ';base64,', b64].join('');
      data.b64data = b64;
    }
    this.log.d('  - ' + (Date.now() - decStart) + ' ms to getRenderData()');
    this.release(key);

    return Promise.resolve(data);
  }

  public exists(tItem: ITransferItemDownload): Promise<never> {
    return Promise.reject(tItem);
  }

  public clearSpace(type: number): Promise<number> {
    return Promise.resolve(type);
  }

  /** Scoped to one download so concurrent downloads of the same file never share a buffer. */
  private bufferKey(blobtype: string, cachekey: string, uniqid?: string): string {
    const fileKey = `${blobtype}-${cachekey}`;
    return uniqid ? `${fileKey}-${uniqid}` : fileKey;
  }

  private release(key: string): void {
    delete this.blobObj[key];
    delete this.dataObj[key];
    delete this.options[key];
  }

  private getBlobObj(key: string): Blob[] {
    if (this.blobObj[key] == undefined) {
      this.blobObj[key] = [];
    }
    return this.blobObj[key];
  }

  private getDataObj(key: string): Array<number> {
    if (this.dataObj[key] == undefined) {
      this.dataObj[key] = [];
    }
    return this.dataObj[key];
  }
}
